import React, { useState, useEffect, useMemo, FC } from "react";
import { X, Loader2, Film, Tv, Info } from "lucide-react";
import { getUploadUrl, processAndAddToLibrary } from "../../services/firebase-api";
import { getMediaDurationFromUrl } from "./helpers";

interface AddMediaModalProps {
    file: File;
    category: 'intro-outro' | 'audio';
    onClose: () => void;
    onSuccess: (newItem: any) => void;
}

export const AddMediaModal: FC<AddMediaModalProps> = ({ file, category, onClose, onSuccess }) => {
    const [name, setName] = useState(file.name.replace(/\.[^/.]+$/, ""));
    const [type, setType] = useState<'intro' | 'outro'>('intro');
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [duration, setDuration] = useState<number | null>(null);

    const mediaUrl = useMemo(() => URL.createObjectURL(file), [file]);

    useEffect(() => {
        getMediaDurationFromUrl(mediaUrl).then(setDuration);
        return () => URL.revokeObjectURL(mediaUrl);
    }, [mediaUrl]);

    const handleSave = async () => {
        if (!name.trim()) {
            setError("Le nom est obligatoire.");
            return;
        }
        if (duration === null) {
            setError("Impossible de déterminer la durée du média.");
            return;
        }
        setIsProcessing(true);
        setError(null);

        try {
            setStatusMessage("Préparation du téléversement...");
            const uploadConfig: any = await getUploadUrl({ fileName: file.name, contentType: file.type });
            const { uploadUrl, gcsUri } = uploadConfig.data;

            setStatusMessage("Téléversement du fichier...");
            await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });

            setStatusMessage("Sauvegarde du média...");

            const payload: any = {
                gcsUri,
                name: name.trim(),
                category,
                duration: duration.toString(),
            };

            if (category === 'intro-outro') {
                payload.type = type;
            }

            const result: any = await processAndAddToLibrary(payload);

            onSuccess(result.data);
            onClose();

        } catch (err: any) {
            console.error("Failed to add media:", err);
            setError(err.message || "Une erreur est survenue.");
        } finally {
            setIsProcessing(false);
            setStatusMessage("");
        }
    };

    const infoText = useMemo(() => {
        if (duration === null) return "Analyse en cours...";
        const minutes = Math.floor(duration / 60).toString().padStart(2, '0');
        const seconds = Math.floor(duration % 60).toString().padStart(2, '0');
        const fileType = file.type.split('/')[1]?.toUpperCase() || 'MÉDIA';
        return `${fileType} • ${minutes}:${seconds}`;
    }, [file, duration]);

    return (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg flex flex-col border border-gray-700 text-white" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Ajouter un média : {category === 'intro-outro' ? 'Intro/Outro' : 'Audio'}</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-700 text-gray-300" disabled={isProcessing}>
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="bg-black rounded-lg aspect-video flex items-center justify-center">
                        <video src={mediaUrl} controls className="w-full h-full object-contain rounded-lg" />
                    </div>

                    {category === 'intro-outro' && (
                        <div className="p-2 border border-blue-500/30 bg-blue-500/10 rounded-md text-sm text-blue-200 flex items-start gap-3">
                            <Info className="w-4 h-4 mt-0.5 shrink-0" />
                            <span>Pour un résultat optimal, utilisez une vidéo avec un fond transparent (ex: format MOV avec canal alpha).</span>
                        </div>
                    )}
                    <div className="text-center text-gray-400 text-sm font-mono">{infoText}</div>

                    <div>
                        <label htmlFor="media-name" className="text-sm font-medium text-gray-300 block mb-1.5">Nom du média</label>
                        <input
                            type="text"
                            id="media-name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full text-sm p-2.5 border border-gray-600 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={isProcessing}
                        />
                    </div>

                    {category === 'intro-outro' && (
                        <div>
                            <label className="text-sm font-medium text-gray-300 block mb-2">Type</label>
                            <div className="grid grid-cols-2 gap-2">
                                <button onClick={() => setType('intro')} disabled={isProcessing} className={`p-3 rounded-xl text-left transition-all flex items-center gap-3 border ${type === 'intro' ? "bg-white text-black border-white ring-2 ring-offset-2 ring-white ring-offset-gray-900" : "bg-gray-800 border-gray-700 hover:border-gray-500"}`}>
                                    <Film className="w-5 h-5" />
                                    <div className="flex-1"><div className="font-bold text-sm">Intro</div></div>
                                </button>
                                <button onClick={() => setType('outro')} disabled={isProcessing} className={`p-3 rounded-xl text-left transition-all flex items-center gap-3 border ${type === 'outro' ? "bg-white text-black border-white ring-2 ring-offset-2 ring-white ring-offset-gray-900" : "bg-gray-800 border-gray-700 hover:border-gray-500"}`}>
                                    <Tv className="w-5 h-5" />
                                    <div className="flex-1"><div className="font-bold text-sm">Outro</div></div>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-gray-700">
                    {isProcessing ? (
                        <div className="flex items-center justify-center gap-3 text-gray-300">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{statusMessage}</span>
                        </div>
                    ) : (
                        <>
                            {error && <p className="text-sm text-red-400 text-center mb-3">{error}</p>}
                            <div className="flex justify-end gap-3">
                                <button onClick={onClose} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-md text-sm font-medium transition-colors">Annuler</button>
                                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-md text-sm font-medium transition-colors">Enregistrer et traiter</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};